import { plainVerdict } from './plain-language.js';
import { verdict } from './verdict.js';
import { generates, overcomes } from './hexagram.js';

/** 仅供内部报告检查页使用；不参与排盘、判断或付费逻辑。 */
export const REPORT_DEBUG_AUDIT = Object.freeze([
  Object.freeze({
    key: 'plainChain', status: '可展示，仍需体验复核',
    note: '已有白话推理链；不新增结论，但仍需检查重复和阅读价值。'
  }),
  Object.freeze({
    key: 'technicalKeyPoints', status: '可直接展示',
    note: '面向懂六爻的用户，内容可逐项复核。'
  }),
  Object.freeze({
    key: 'useGodRelations', status: '需要白话化',
    note: '结构完整，但当前是对象数据，不适合直接给普通用户。'
  }),
  Object.freeze({
    key: 'interactions', status: '需要分组，禁止强弱排序',
    note: '当前是全量关系；可按直接/间接分组，尚无规则判断哪条更重要。'
  }),
  Object.freeze({
    key: 'timingCandidates', status: '需要白话化，禁止选主日期',
    note: '所有规则候选并列保留；confidence 是依据强度，不是发生概率。'
  }),
  Object.freeze({
    key: 'sources', status: '出处覆盖不完整',
    note: '目前只汇总应期候选的规则出处，不代表整份报告都有溯源。'
  })
]);

const uniqueSources = (timing) => {
  const seen = new Set();
  const sources = [];
  for (const candidate of timing?.candidates || []) {
    for (const rule of candidate.rules || []) {
      const source = rule.source;
      if (!source?.book) continue;
      const key = `${source.book}\u0000${source.chapter || ''}\u0000${source.note || ''}`;
      if (seen.has(key)) continue;
      seen.add(key);
      sources.push({ ...source });
    }
  }
  return sources;
};

const sourceKey = (source) => (
  `${source?.book || ''}\u0000${source?.chapter || ''}\u0000${source?.note || ''}`
);

const lineLabel = (line) => {
  if (!line) return '未知爻';
  return `${line.position}爻${line.relative}${line.ganzhi}`
    + `${line.isShi ? '（世爻）' : line.isYing ? '（应爻）' : ''}`;
};

const interactionText = (interaction, result) => {
  const from = result.yao[interaction.from - 1];
  const to = result.yao[interaction.to - 1];
  const activity = interaction.activity === '暗动' || (!from?.moving && from?.marks?.includes('暗动'))
    ? '暗动' : from?.moving ? '发动' : '';
  return `${lineLabel(from)}${activity}${interaction.type}${lineLabel(to)}。`;
};

const relationFactText = (relation, type, isHidden) => {
  const source = `${relation.sourceKind || ''}${lineLabel(relation.from)}`;
  const target = `${isHidden ? '伏神用神' : '用神'}${lineLabel(relation.target)}`;
  if (type === '克') {
    const rescue = relation.救神?.length
      ? `；另有${relation.救神.map(lineLabel).join('、')}克制该来源`
      : '；未见动爻克制该来源';
    return `${source}克${target}${rescue}。`;
  }
  const restraint = relation.制约者?.length
    ? `；但${relation.制约者.map(lineLabel).join('、')}克制该来源`
    : '；未见动爻克制该来源';
  return `${source}生${target}${restraint}。`;
};

const combinationText = (combination) => {
  const action = combination.kind === '动变作合' ? '动变后与' : '与';
  return `${lineLabel(combination.first)}${action}${lineLabel(combination.second)}`
    + `地支作合，当前记为${combination.combineState}。`;
};

const uniqueText = (items) => [...new Set(items.filter(Boolean))];

function buildTechnicalPreviewTerms(result, technical) {
  const useGod = technical.facts?.用神;
  const primary = useGod?.primary;
  const useValue = primary
    ? `${useGod.relative}（${useGod.element}） · ${useGod.isAbsent ? `伏于${primary.position}爻下` : `${primary.position}爻`}`
    : '尚未取定';
  const movingValue = primary
    ? `${primary.moving ? '用神发动' : '用神安静'} · ${result.moving.length ? `${result.moving.length}爻动` : '六爻安静'}`
    : result.moving.length ? `${result.moving.length}爻动` : '六爻安静';
  const marks = uniqueText([
    primary?.transform?.kind,
    ...(technical.facts?.特殊状态 || []),
    ...(technical.facts?.卦象 || [])
  ]).slice(0, 3);
  return [
    { label: '取用', value: useValue },
    { label: '旺衰', value: primary?.state || '未定' },
    { label: '动静', value: movingValue },
    { label: '标记', value: marks.length ? marks.join(' · ') : '无特殊标记' }
  ];
}

const DIRECTION_STATE = Object.freeze({
  旺: { vote: 1, text: '代表这件事的力量目前较强' },
  相: { vote: 1, text: '代表这件事的力量得到当月环境支持' },
  休: { vote: 0, text: '代表这件事的基础力量一般' },
  囚: { vote: -1, text: '代表这件事的力量目前受限' },
  死: { vote: -1, text: '代表这件事的力量受当月环境压制' },
  平: { vote: 0, text: '代表这件事的力量目前普通' }
});
const POSITIVE_TRANSFORMS = new Set(['回头生', '化进神']);
const NEGATIVE_TRANSFORMS = new Set(['回头克', '化退神', '化绝', '化空', '化墓']);

/**
 * 面向普通用户的保守方向语。不调用已废弃的 heuristicScore，
 * 只把用神当前状态、直接动爻生克和用神自身动变各记一票。
 */
export function buildDirectionalSummary(result, technical = verdict(result)) {
  const facts = technical.facts?.用神;
  const primary = facts?.primary;
  const note = '仅为易学规则下的大致倾向，不代表必然结果。';
  if (!primary) {
    return {
      level: 'unclear', label: '信息不足',
      summary: '当前没有可用的用神证据，暂时不能归纳方向。', basis: [], note
    };
  }

  let balance = 0;
  const basis = [];
  const state = DIRECTION_STATE[primary.state] || DIRECTION_STATE.平;
  balance += state.vote;
  basis.push(state.text);

  if (facts.isAbsent) {
    balance -= 1;
    basis.push('相关条件尚未直接显现');
  }
  if (technical.facts?.得生?.length) {
    balance += 1;
    basis.push('已有变化中的力量直接提供帮助');
  }
  if (technical.facts?.受克?.length) {
    balance -= 1;
    basis.push('已有变化中的力量直接形成阻力');
  }

  const transform = primary.transform?.kind;
  if (POSITIVE_TRANSFORMS.has(transform)) {
    balance += 1;
    basis.push('代表这件事的力量变化后得到帮助');
  } else if (NEGATIVE_TRANSFORMS.has(transform)) {
    balance -= 1;
    basis.push('代表这件事的力量变化后受到压制');
  }

  const severe = (technical.facts?.特殊状态 || []).find((item) => (
    !item.includes('空而不空') && /旬空|月破|日破|入墓|入绝/u.test(item)
  ));
  if (severe) {
    if (state.vote >= 0) balance -= 1;
    basis.push('另有暂时难落实或难发挥的情况');
  }

  const level = balance > 0 ? 'favorable' : balance < 0 ? 'obstructed' : 'mixed';
  const label = level === 'favorable' ? '偏有利' : level === 'obstructed' ? '偏受阻' : '条件相持';
  const closing = level === 'favorable'
    ? '整体更接近“可以继续推进，但要边做边验证”。'
    : level === 'obstructed'
      ? '整体更接近“当前推进阻力较大，需先补足关键条件”。'
      : '整体更接近“有机会，但成败要看后续条件能否落实”。';
  return {
    level, label,
    summary: `${basis.slice(0, 2).join('；')}。${closing}`,
    basis, note
  };
}

function buildUseGodDraft(result, technical) {
  const facts = technical.facts?.用神;
  if (!facts?.primary) {
    return {
      title: '用神', anchor: '当前没有可展示的用神数据。', selection: null, facts: []
    };
  }
  const primary = facts.primary;
  const anchor = facts.isAbsent
    ? `用神${facts.relative}没有直接出现在本卦六爻中，取伏神${lineLabel(primary)}作为观察点。`
    : `本次取${lineLabel(primary)}为主用神。`;
  let selection = null;
  if (facts.isAbsent && facts.flying) {
    selection = `伏神位于${lineLabel(facts.flying)}之下，飞伏关系放在“关键关系”中列出。`;
  } else if (facts.auxiliaries?.length) {
    selection = `另有${facts.auxiliaries.map(lineLabel).join('、')}为辅；取主依据：${facts.selectionReason || '现有取用顺序'}。`;
  }
  const details = [
    `问事类别：${result.category || '未选择'}；取用六亲：${facts.relative}；五行：${facts.element}。`,
    `旺衰：${primary.state || '未提供'}；动静：${primary.moving ? '发动' : '安静'}。`
  ];
  if (primary.isShi || primary.isYing) {
    details.push(`爻位身份：${primary.isShi ? '世爻' : '应爻'}。`);
  }
  if (primary.transform) {
    details.push(
      `动变：变为${primary.transform.toRelative}${primary.transform.to}`
      + `，类型为${primary.transform.kind}`
      + `，变爻旺衰${primary.transform.toState}`
      + `${primary.transform.toVoid ? '，并且化空' : ''}。`
    );
  }
  return { title: '用神', anchor, selection, facts: details };
}

function buildRelationsDraft(result, technical) {
  const useGod = technical.facts?.用神;
  const primaryPosition = useGod?.primary?.position;
  const factKeys = new Set();
  const direct = [];
  for (const relation of technical.facts?.受克 || []) {
    factKeys.add(`${relation.from.position}:${relation.target.position}:克`);
    direct.push(relationFactText(relation, '克', useGod?.isAbsent));
  }
  for (const relation of technical.facts?.得生 || []) {
    factKeys.add(`${relation.from.position}:${relation.target.position}:生`);
    direct.push(relationFactText(relation, '生', useGod?.isAbsent));
  }

  const other = [];
  for (const interaction of result.interactions || []) {
    const touchesVisibleUseGod = !useGod?.isAbsent
      && (interaction.from === primaryPosition || interaction.to === primaryPosition);
    const key = `${interaction.from}:${interaction.to}:${interaction.type}`;
    if (touchesVisibleUseGod && !factKeys.has(key)) direct.push(interactionText(interaction, result));
    if (!touchesVisibleUseGod) other.push(interactionText(interaction, result));
  }

  const staticRelations = [];
  const useElement = useGod?.primary?.element;
  for (const line of result.yao || []) {
    if (!useElement || (!useGod?.isAbsent && line.position === primaryPosition)) continue;
    if (line.moving || line.marks?.includes('暗动')) continue;
    const target = `${useGod?.isAbsent ? '伏神用神' : '用神'}${lineLabel(useGod.primary)}`;
    if (generates(line.element, useElement)) {
      staticRelations.push(`${lineLabel(line)}五行生${target}，该爻当前安静。`);
    } else if (overcomes(line.element, useElement)) {
      staticRelations.push(`${lineLabel(line)}五行克${target}，该爻当前安静。`);
    }
  }

  const structural = [
    ...(result.patterns || []).map((pattern) => `卦象结构：${pattern}。`),
    ...(result.lineCombinations || []).map(combinationText),
    ...(result.tripleCombineRelations || []).map(({ from, to, type }) => `${from}${type}${to}。`)
  ];
  const pendingCount = (technical.facts?.复合事实 || []).length;
  return {
    title: '关键关系',
    note: '只分组，不判断哪条更强，也不据此裁决吉凶。',
    direct: uniqueText(direct),
    static: uniqueText(staticRelations),
    other: uniqueText(other),
    structural: uniqueText(structural),
    pending: pendingCount ? `${pendingCount} 条复合事实仍待白话化，暂不混入正文。` : null
  };
}

function buildTimingDraft(timing, sourceNumbers, timeScope = null) {
  const scaleMeta = {
    day: { label: '日候选', hint: '适用于明确关注近期进展的问题。' },
    month: { label: '月候选', hint: '只有问题本身允许按月观察时再看。' },
    year: { label: '年候选', hint: '只有明确的长期问题才适用，不能与日候选混作同一层。' }
  };
  const groups = ['day', 'month', 'year'].map((scale) => ({
    scale,
    ...scaleMeta[scale],
    items: (timing?.candidates || [])
      .filter((candidate) => candidate.scale === scale)
      .map((candidate) => ({
        date: `${candidate.date.year}-${String(candidate.date.month).padStart(2, '0')}`
          + `-${String(candidate.date.day).padStart(2, '0')}`,
        ganzhi: candidate.ganzhi,
        daysAway: candidate.daysAway,
        reasons: uniqueText((candidate.rules || []).map(({ name }) => name)),
        sourceRefs: [...new Set((candidate.rules || [])
          .map(({ source }) => sourceNumbers.get(sourceKey(source)))
          .filter(Boolean))]
      }))
  })).filter(({ items }) => items.length);
  return {
    title: '时间候选',
    note: timing?.note || '候选日期仅按规则列出，不代表事情一定在该日发生。',
    orderNote: timeScope === 'none'
      ? '本次选择“不问时间”，因此不展示日期候选。'
      : '按日期远近排列，不按重要性或发生概率排序。',
    groups,
    unresolved: (timing?.unresolved || []).map(({ ruleId, reason }) => ({ ruleId, reason }))
  };
}

function buildPlainTimingSection(result, timingDraft) {
  if (result.timeScope === 'none') return null;
  const scopeLabels = { near: '近期', mid: '中期', long: '长期' };
  const timingLabels = timingDraft.groups.flatMap(({ scale, items }) => items.map(({ date }) => {
    const [year, month, day] = date.split('-').map(Number);
    if (scale === 'year') return `${year}年前后`;
    if (scale === 'month') return `${year}年${month}月前后`;
    return `${year}年${month}月${day}日`;
  }));
  const uniqueLabels = [...new Set(timingLabels)];
  if (!uniqueLabels.length) {
    return {
      key: 'timing', title: '时间参考',
      text: `按你选择的“${scopeLabels[result.timeScope] || '所选'}”范围，目前没有可换算的观察时间；后续如列出日期也只作参考，不是保证发生的日期。`
    };
  }
  const shown = uniqueLabels.slice(0, 3);
  const remaining = uniqueLabels.length - shown.length;
  return {
    key: 'timing', title: '时间参考',
    text: `按你选择的“${scopeLabels[result.timeScope] || '所选'}”范围，可先留意${shown.join('、')}`
      + `${remaining > 0 ? `等${uniqueLabels.length}个时间点` : ''}。这些只是按规则列出的观察节点，不是保证发生的日期。`
  };
}

/**
 * 内部付费报告原型：只压缩、分组已有字段，不补判断、不选主日期。
 */
export function buildEvidenceDraft(result, timing = null, technical = verdict(result)) {
  const scalesByScope = {
    none: [], near: ['day'], mid: ['day', 'month'], long: ['month', 'year']
  };
  const allowedScales = scalesByScope[result.timeScope] || null;
  const scopedTiming = allowedScales
    ? {
        ...timing,
        candidates: (timing?.candidates || []).filter(({ scale }) => allowedScales.includes(scale)),
        unresolved: result.timeScope === 'none' ? [] : (timing?.unresolved || [])
      }
    : timing;
  const sources = uniqueSources(scopedTiming).map((source, index) => ({
    number: index + 1,
    ...source
  }));
  const sourceNumbers = new Map(sources.map((source) => [sourceKey(source), source.number]));
  return {
    note: '这是已有证据的整理稿；上方方向是保守归纳，不等于确定吉凶，也不替用户做现实决策。',
    useGod: buildUseGodDraft(result, technical),
    relations: buildRelationsDraft(result, technical),
    timing: buildTimingDraft(scopedTiming, sourceNumbers, result.timeScope),
    sources: {
      title: '出处',
      note: '当前出处只覆盖时间候选；其他排盘与生克事实的逐条出处仍待补齐。',
      items: sources
    }
  };
}

/**
 * 只重组已有排盘与证据，不新增判断。preview/report 是未来付费墙的结构边界。
 */
export function buildReportView(result, timing = null) {
  const technical = verdict(result);
  const plainBase = plainVerdict(result);
  const evidenceDraft = buildEvidenceDraft(result, timing, technical);
  const timingSection = buildPlainTimingSection(result, evidenceDraft.timing);
  const plain = {
    ...plainBase,
    sections: [...plainBase.sections, ...(timingSection ? [timingSection] : [])]
  };
  const direction = buildDirectionalSummary(result, technical);
  return {
    preview: {
      hexagram: result.primary,
      changed: result.changed || null,
      yao: result.yao.map((line) => ({
        position: line.position,
        ganzhi: line.ganzhi,
        relative: line.relative,
        moving: Boolean(line.moving)
      })),
      shiYing: { shi: result.primary.shi, ying: result.primary.ying },
      moving: [...(result.moving || [])],
      terms: buildTechnicalPreviewTerms(result, technical),
      direction,
      summary: plain.sections.slice(0, 2).map(({ text }) => text).join(''),
      coreEvidence: technical.keyPoints.slice(0, 2)
    },
    report: {
      plainChain: plain.chain,
      technicalKeyPoints: technical.keyPoints,
      useGodRelations: technical.facts?.用神 || result.useGod || null,
      interactions: result.interactions || [],
      timingCandidates: timing?.candidates || [],
      sources: uniqueSources(timing)
    },
    evidenceDraft,
    plain,
    technical
  };
}
